import { useEffect, useState } from 'react';
import { homes as homesApi, bridges as bridgesApi, type Home, type Bridge } from '../../lib/api.js';
import { useAuth } from '../../context/AuthContext.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { useToast } from '../../components/ui/Toast.js';

export default function HomesSettingsPage() {
  const { currentHomeId, accessToken, refresh, setCurrentHomeId } = useAuth();
  const toast = useToast();
  const [homes, setHomes] = useState<Home[]>([]);
  const [bridges, setBridges] = useState<Bridge[]>([]);
  const [newHomeName, setNewHomeName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [bridgeLabel, setBridgeLabel] = useState('');
  const [showAddBridge, setShowAddBridge] = useState(false);
  const [pairingCode, setPairingCode] = useState<string | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    homesApi
      .list()
      .then((r) => setHomes(r.homes))
      .catch(() => {});
  }, [accessToken]);

  useEffect(() => {
    if (!currentHomeId) return;
    bridgesApi
      .list(currentHomeId)
      .then((r) => setBridges(r.bridges))
      .catch(() => {});
  }, [currentHomeId]);

  const createHome = async () => {
    if (!newHomeName) return;
    try {
      const h = await homesApi.create(newHomeName);
      setHomes((list) => [...list, h]);
      setNewHomeName('');
      // First home? Auto-select it + pull fresh session so primaryHomeId lands.
      setCurrentHomeId(h.id);
      await refresh();
      toast.show('Home created', 'success');
    } catch {
      toast.show('Failed to create home', 'error');
    }
  };

  const sendInvite = async () => {
    if (!currentHomeId || !inviteEmail) return;
    try {
      await homesApi.invite(currentHomeId, inviteEmail, inviteRole);
      setInviteEmail('');
      toast.show('Invite sent', 'success');
    } catch {
      toast.show('Failed to invite', 'error');
    }
  };

  const addBridge = async () => {
    if (!currentHomeId || !bridgeLabel) return;
    try {
      const r = await bridgesApi.create(currentHomeId, bridgeLabel);
      setBridges((list) => [...list, r.bridge]);
      setPairingCode(r.pairingCode);
      setBridgeLabel('');
    } catch {
      toast.show('Failed to create bridge', 'error');
    }
  };

  const regeneratePairingCode = async (bridgeId: string) => {
    if (!currentHomeId) return;
    try {
      const r = await bridgesApi.newPairingCode(currentHomeId, bridgeId);
      setPairingCode(r.pairingCode);
      setShowAddBridge(true);
    } catch {
      toast.show('Failed to regenerate code', 'error');
    }
  };

  const deleteBridge = async (bridgeId: string, label: string) => {
    if (!currentHomeId) return;
    if (!window.confirm(`Remove bridge "${label}"? This unpairs it from AetherCasa.`)) return;
    try {
      await bridgesApi.delete(currentHomeId, bridgeId);
      setBridges((list) => list.filter((b) => b.id !== bridgeId));
      toast.show('Bridge removed', 'success');
    } catch {
      toast.show('Failed to remove bridge', 'error');
    }
  };

  const deleteHome = async (home: Home) => {
    const answer = window.prompt(
      `Type the home name "${home.name}" to permanently delete it. This removes all members, invites, bridges, and history.`,
    );
    if (answer !== home.name) {
      if (answer !== null) toast.show('Name did not match — not deleted', 'error');
      return;
    }
    try {
      await homesApi.delete(home.id);
      setHomes((list) => list.filter((h) => h.id !== home.id));
      if (currentHomeId === home.id) {
        await refresh();
      }
      toast.show('Home deleted', 'success');
    } catch {
      toast.show('Failed to delete home', 'error');
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 flex flex-col gap-8">
      <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
        Homes
      </h1>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm uppercase tracking-wide" style={{ color: 'var(--ac-text-muted)' }}>
          Your homes
        </h2>
        <ul className="flex flex-col gap-2">
          {homes.map((h) => (
            <li
              key={h.id}
              className="p-3 rounded-lg border flex items-center justify-between gap-3"
              style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
            >
              <span style={{ color: 'var(--ac-text)' }}>
                {h.glyph ?? '🏠'} {h.name}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs" style={{ color: 'var(--ac-text-muted)' }}>
                  {h.role}
                </span>
                {h.role === 'owner' ? (
                  <Button variant="danger" onClick={() => deleteHome(h)}>
                    Delete
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            value={newHomeName}
            onChange={(e) => setNewHomeName(e.target.value)}
            placeholder="New home name"
            className="flex-1 p-2 rounded-lg border text-sm"
            style={{
              backgroundColor: 'var(--ac-bg)',
              borderColor: 'var(--ac-border)',
              color: 'var(--ac-text)',
            }}
          />
          <Button onClick={createHome} disabled={!newHomeName}>
            Create
          </Button>
        </div>
      </section>

      {currentHomeId ? (
        <>
          <section className="flex flex-col gap-3">
            <h2
              className="text-sm uppercase tracking-wide"
              style={{ color: 'var(--ac-text-muted)' }}
            >
              Invite members
            </h2>
            <div className="flex gap-2">
              <input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@example.com"
                className="flex-1 p-2 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--ac-bg)',
                  borderColor: 'var(--ac-border)',
                  color: 'var(--ac-text)',
                }}
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="p-2 rounded-lg border text-sm"
                style={{
                  backgroundColor: 'var(--ac-bg)',
                  borderColor: 'var(--ac-border)',
                  color: 'var(--ac-text)',
                }}
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="guest">Guest</option>
              </select>
              <Button onClick={sendInvite} disabled={!inviteEmail}>
                Invite
              </Button>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2
              className="text-sm uppercase tracking-wide"
              style={{ color: 'var(--ac-text-muted)' }}
            >
              Bridges
            </h2>
            <div
              className="p-3 rounded-lg border text-sm flex items-start justify-between gap-3"
              style={{
                backgroundColor: 'var(--ac-surface)',
                borderColor: 'var(--ac-border)',
                color: 'var(--ac-text)',
              }}
            >
              <div>
                <p className="font-medium">Approve a waiting Home Assistant bridge</p>
                <p className="text-xs mt-1" style={{ color: 'var(--ac-text-muted)' }}>
                  Enter the activation code shown by the AetherCasa add-on. Home Assistant
                  credentials stay inside your Home Assistant installation.
                </p>
              </div>
              <a
                href="/activate"
                className="shrink-0 rounded px-3 py-1.5 text-xs font-medium"
                style={{ background: 'var(--ac-accent)', color: 'var(--ac-on-accent)' }}
              >
                Enter code
              </a>
            </div>
            <ul className="flex flex-col gap-2">
              {bridges.map((b) => (
                <li
                  key={b.id}
                  className="p-3 rounded-lg border flex items-center justify-between gap-3"
                  style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
                >
                  <span style={{ color: 'var(--ac-text)' }}>{b.label}</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: b.connected ? 'var(--ac-success)' : 'var(--ac-border)',
                        color: 'white',
                      }}
                    >
                      {b.connected ? 'online' : 'offline'}
                    </span>
                    <Button variant="secondary" onClick={() => regeneratePairingCode(b.id)}>
                      New code
                    </Button>
                    <Button variant="danger" onClick={() => deleteBridge(b.id, b.label)}>
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <Button variant="secondary" onClick={() => setShowAddBridge(true)}>
              Add bridge
            </Button>
          </section>

          <Modal
            open={showAddBridge}
            onClose={() => {
              setShowAddBridge(false);
              setPairingCode(null);
            }}
            title="Add bridge"
          >
            {pairingCode ? (
              <div className="flex flex-col gap-3">
                <p style={{ color: 'var(--ac-text)' }}>
                  Use this pairing code on your bridge. It will be shown only once.
                </p>
                <code
                  className="block p-3 rounded-lg text-center text-lg font-mono"
                  style={{ backgroundColor: 'var(--ac-surface-elevated)', color: 'var(--ac-text)' }}
                >
                  {pairingCode}
                </code>
                <div
                  className="text-sm p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--ac-surface-elevated)',
                    color: 'var(--ac-text-muted)',
                  }}
                >
                  <strong style={{ color: 'var(--ac-text)' }}>Where to paste it</strong>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>
                      <strong>Home Assistant Add-on:</strong> HA → Settings → Add-ons → AetherCasa
                      Bridge → Configuration → <code>pairing_code</code>.{' '}
                      <a
                        href="https://aethercasa.com/docs"
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: 'var(--ac-accent)' }}
                      >
                        Install guide →
                      </a>
                    </li>
                    <li>
                      <strong>Docker:</strong> pass as <code>AETHERCASA_PAIRING_CODE</code> on first
                      run of <code>ghcr.io/aethercasa/aethercasa-bridge:latest</code>.
                    </li>
                  </ul>
                  <div className="mt-2 text-xs">Expires in 15 minutes.</div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <input
                  value={bridgeLabel}
                  onChange={(e) => setBridgeLabel(e.target.value)}
                  placeholder="Bridge label (e.g. Home Assistant)"
                  className="w-full p-2 rounded-lg border text-sm"
                  style={{
                    backgroundColor: 'var(--ac-bg)',
                    borderColor: 'var(--ac-border)',
                    color: 'var(--ac-text)',
                  }}
                />
                <Button onClick={addBridge} disabled={!bridgeLabel}>
                  Create
                </Button>
              </div>
            )}
          </Modal>
        </>
      ) : null}
    </div>
  );
}
